/**
 * Textile Compliance Services page content (/textile-compliance-services).
 *
 * Every string in this file is transcribed verbatim from the approved
 * "Textile Compliance Services (Main Page) — SEO Optimized 2026" requirement
 * document (MBA_MERN_Solutions/Textile Compliance Services.docx). Do not
 * reword, shorten or extend this copy — update the document first.
 *
 * NOTE the document also carries a per-service SEO Title and Meta Description
 * for each of the six services. Those are page metadata for future dedicated
 * sub-pages, not body copy, so they are NOT rendered here — only this page's
 * own title and description are used, from `textileSeo` below.
 *
 * ON THE RCS AND OCS MARKS: the project holds one combined "RCS & OCS" logo
 * file, which is the mark as supplied. Both cards therefore point at the same
 * `rcs-ocs` key — that is the real mark for both, not a stand-in.
 *
 * `image` paths point at the illustrations already in
 * public/assets/certification-process. No new artwork was introduced.
 */

export const textileSeo = {
  title:
    "Textile Certification Consultancy | GOTS, GRS, WRAP, Sedex & Sustainable Compliance | MBA Solutions",
  description:
    "MBA Solutions provides textile compliance consultancy including GOTS, GRS, RCS, OCS, WRAP and Sedex/SMETA certification support for textile manufacturers and exporters.",
  slug: "/textile-compliance-services",
};

export const heroContent = {
  h1: "Building Sustainable & Responsible Textile Supply Chains",
  subtitle: "Helping Textile Businesses Meet Global Buyer Requirements",
  paragraphs: [
    "MBA Solutions supports textile manufacturers, exporters, suppliers and supply chain partners in achieving internationally recognized textile compliance and sustainability certifications.",
    "With increasing demand for responsible sourcing, ethical manufacturing and sustainable production, businesses need strong compliance systems to access global markets.",
    "Our experts provide complete support from assessment and documentation to implementation, training and certification preparation.",
  ],
};

export const servicesContent = {
  title: "Our Textile Compliance Services",
  standards: [
    {
      id: "gots",
      logoKey: "gots",
      title: "1. GOTS Certification",
      headline:
        "GOTS Certification – Supporting Organic & Sustainable Textile Production",
      paragraphs: [
        "The Global Organic Textile Standard (GOTS) is one of the world’s leading textile processing standards for organic fibers and sustainable production.",
        "GOTS certification ensures that textile products meet strict environmental and social criteria throughout the supply chain — from raw materials to finished products.",
        "MBA Solutions helps textile organizations prepare for GOTS certification by developing compliant systems and improving operational practices.",
      ],
      listLabel: "Our GOTS Consultancy Includes:",
      items: [
        "GOTS requirement assessment",
        "Supply chain evaluation",
        "Documentation preparation",
        "Process improvement support",
        "Compliance guidance",
        "Certification audit preparation",
      ],
      benefitsLabel: "Benefits of GOTS Certification:",
      benefits: [
        "Access to global sustainable markets",
        "Increased buyer confidence",
        "Proof of organic textile practices",
        "Improved environmental performance",
        "Strong sustainability positioning",
      ],
    },
    {
      id: "grs",
      logoKey: "grs",
      title: "2. GRS Certification",
      headline: "GRS Certification – Demonstrating Responsible Recycled Production",
      paragraphs: [
        "The Global Recycled Standard (GRS) helps organizations verify recycled content, responsible production practices and transparent supply chains.",
        "MBA Solutions supports textile manufacturers in implementing GRS requirements and preparing for certification.",
      ],
      listLabel: "Our GRS Consultancy Includes:",
      items: [
        "Recycled material assessment",
        "Process evaluation",
        "Traceability system development",
        "Documentation support",
        "Compliance improvement",
        "Audit preparation",
      ],
      benefitsLabel: "Benefits of GRS Certification:",
      benefits: [
        "Verification of recycled content",
        "Better supply chain transparency",
        "Improved sustainability credibility",
        "Meets global buyer expectations",
        "Supports circular economy goals",
      ],
    },
    {
      id: "rcs",
      logoKey: "rcs-ocs",
      title: "3. RCS Certification",
      headline: "RCS Certification – Verifying Recycled Material Claims",
      paragraphs: [
        "The Recycled Claim Standard (RCS) provides verification of recycled input materials and helps businesses communicate accurate sustainability claims.",
        "MBA Solutions helps organizations establish the required systems for RCS compliance.",
      ],
      listLabel: "Our RCS Support:",
      items: [
        "Material verification guidance",
        "Supply chain assessment",
        "Documentation preparation",
        "Traceability support",
        "Certification readiness",
      ],
      benefitsLabel: "Benefits of RCS Certification:",
      benefits: [
        "Transparent recycled content claims",
        "Increased customer trust",
        "Better market acceptance",
        "Supports sustainable product development",
      ],
    },
    {
      id: "ocs",
      logoKey: "rcs-ocs",
      title: "4. OCS Certification",
      headline: "OCS Certification – Verifying Organic Textile Content",
      paragraphs: [
        "The Organic Content Standard (OCS) verifies the presence and amount of organic material in textile products.",
        "MBA Solutions supports businesses in implementing OCS requirements and preparing for certification assessment.",
      ],
      listLabel: "Our OCS Consultancy Includes:",
      items: [
        "Organic material assessment",
        "Supply chain review",
        "Documentation support",
        "Compliance guidance",
        "Audit preparation",
      ],
      benefitsLabel: "Benefits of OCS Certification:",
      benefits: [
        "Verified organic content",
        "Better customer confidence",
        "Supports sustainable sourcing",
        "Improved product credibility",
      ],
    },
    {
      id: "wrap",
      logoKey: "wrap",
      title: "5. WRAP Certification",
      headline: "WRAP Certification – Promoting Ethical Manufacturing Practices",
      paragraphs: [
        "WRAP (Worldwide Responsible Accredited Production) focuses on ethical manufacturing, workplace safety and responsible business practices.",
        "MBA Solutions helps textile factories prepare systems required for WRAP certification.",
      ],
      listLabel: "Our WRAP Consultancy Includes:",
      items: [
        "Social compliance assessment",
        "Workplace practice review",
        "Policy development",
        "Employee awareness",
        "Audit preparation",
      ],
      benefitsLabel: "Benefits of WRAP Certification:",
      benefits: [
        "Demonstrates ethical production",
        "Improves buyer confidence",
        "Supports global sourcing requirements",
        "Strengthens workplace practices",
      ],
    },
    {
      id: "sedex-smeta",
      logoKey: "sedex-smeta",
      title: "6. Sedex / SMETA Certification",
      headline: "Sedex / SMETA – Strengthening Ethical Supply Chains",
      paragraphs: [
        "Sedex and SMETA help organizations demonstrate responsible business practices, ethical labor standards and supply chain transparency.",
        "MBA Solutions supports companies in preparing for SMETA audits and improving social compliance performance.",
      ],
      listLabel: "Our Sedex / SMETA Support:",
      items: [
        "Pre-audit assessment",
        "Social compliance review",
        "Documentation support",
        "Corrective action planning",
        "Audit preparation",
      ],
      benefitsLabel: "Benefits of Sedex / SMETA:",
      benefits: [
        "Improved ethical compliance",
        "Better buyer relationships",
        "Stronger supply chain transparency",
        "Support for global sourcing requirements",
      ],
    },
  ],
};

export const processContent = {
  title: "Our Textile Certification Process",
  steps: [
    {
      number: "1",
      image: "/assets/certification-process/consultation.webp",
      title: "Step 1: Consultation",
      description:
        "Understanding your products, supply chain and certification goals.",
    },
    {
      number: "2",
      image: "/assets/certification-process/gap-assessment.webp",
      title: "Step 2: Gap Assessment",
      description:
        "Reviewing your current system against standard requirements.",
    },
    {
      number: "3",
      image: "/assets/certification-process/documentation.webp",
      title: "Step 3: Documentation",
      description: "Preparing policies, procedures and compliance records.",
    },
    {
      number: "4",
      image: "/assets/certification-process/implementation.webp",
      title: "Step 4: Implementation",
      description: "Supporting your team in applying required practices.",
    },
    {
      number: "5",
      image: "/assets/certification-process/internal-audit.webp",
      title: "Step 5: Internal Review",
      description: "Checking readiness before certification assessment.",
    },
    {
      number: "6",
      image: "/assets/certification-process/certification-support.webp",
      title: "Step 6: Certification Support",
      description:
        "Helping you successfully complete the certification process.",
    },
  ],
};

export const industriesContent = {
  title: "Industries We Support",
  intro: "MBA Solutions works with:",
  industries: [
    { icon: "textile", name: "Textile Manufacturers" },
    { icon: "garment", name: "Garment Factories" },
    { icon: "export", name: "Apparel Exporters" },
    { icon: "fabric", name: "Fabric Producers" },
    { icon: "spinning", name: "Spinning Mills" },
    { icon: "processing", name: "Processing Units" },
    { icon: "sustainableFashion", name: "Sustainable Fashion Brands" },
    { icon: "supplyChain", name: "Supply Chain Partners" },
  ],
};

export const whyChooseContent = {
  title: "Why Choose MBA Solutions?",
  reasons: [
    "Textile Compliance Expertise",
    "Sustainability-Focused Approach",
    "International Standards Knowledge",
    "Complete Certification Support",
    "Practical Business Solutions",
  ],
};

export const finalCtaContent = {
  title: "Ready To Build A Sustainable Textile Business?",
  paragraph:
    "Partner with MBA Solutions to achieve textile certifications, improve compliance and meet global buyer expectations.",
  cta: { label: "Get Textile Compliance Consultation Today", to: "/contact" },
};
